import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from 'i18next';

// MUI
import Box from '@mui/material/Box';
import Grid from "@mui/material/Grid";
import Card from '@mui/material/Card';
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material";

// Custom Components
import AppPage from "@/ui/AppPage/AppPage";
import { Permissions } from "@/lib/permissions";
import api from "@/lib/api";
import BarChart from "@/ui/Charts/BarChart"

// Types
import { DCRecord } from "@/@types/lib/dataController";

const ROUTE_STATS_PATH = "hps/izleti-stats"

type GraphDataType = {
  izleti_count: {
    labels: string[]
    series_hps: number[]
    series_registered_user: number[]
  },
  new_users_count: {
    labels: string[]
    series_hps: number[]
    series_registered_user: number[]
  }
}

type Record = {
  month_label: string,
  hps_user_count: number,
  reg_user_count: number,
  hps_izlet_count: number,
  reg_izlet_count: number
}

const HpsStatistikaPage = () => {

  const apiInstance = new api();
  const theme = useTheme();
  const { t } = useTranslation();

  const [rawStatsData, setRawStatsData] = useState<DCRecord | undefined>(undefined);

  const [graphData, setGraphData] = useState<GraphDataType | undefined>(undefined);

  useEffect(() => {
    getStats();
  }, [])

  useEffect(() => {
    if (rawStatsData)
      parseStats();
  }, [rawStatsData])

  const getStats = () => {
    apiInstance.Call(ROUTE_STATS_PATH, "get").then((resp) => {
      const data = resp.data ? resp.data as DCRecord : undefined;
      setRawStatsData(data);
    })
  }

  const parseStats = () => {

    const newGraphDataHPS: GraphDataType = {
      izleti_count: { labels: [], series_hps: [], series_registered_user: [] },
      new_users_count: { labels: [], series_hps: [], series_registered_user: [] }
    };

    if (rawStatsData) {
      for (let i = 12; i > 0; i -= 1) {
        const record = rawStatsData[i] as Record;

        newGraphDataHPS.new_users_count.labels.push(record.month_label);
        newGraphDataHPS.new_users_count.series_hps.push(record.hps_user_count);
        newGraphDataHPS.new_users_count.series_registered_user.push(record.reg_user_count);

        newGraphDataHPS.izleti_count.labels.push(record.month_label);
        newGraphDataHPS.izleti_count.series_hps.push(record.hps_izlet_count);
        newGraphDataHPS.izleti_count.series_registered_user.push(record.reg_izlet_count);
      }

      setGraphData(newGraphDataHPS);
    }
  }

  const monthGraphOptions = {
    plotOptions: {
      bar: {
        horizontal: false
      }
    }
  }

  i18n.on('languageChanged', parseStats);

  return (
    <AppPage needPermission={[Permissions.Administration, Permissions.HPSKomisijaView]}>
      <Box m={2}>
        <Grid container spacing={2}>
          <Grid container size={{xs: 12, lg: 6}} spacing={2}>
            <Grid size={12}>
              <Card sx={{ overflowX: "auto" }} >
                <Typography p={2} variant="h5">{t('stats.created-users')}</Typography>
                {graphData ?
                  <BarChart
                    height="295px"
                    width="100%"
                    series={[
                      { name: t("organisations.hps"), data: graphData.new_users_count.series_hps, color: theme.palette.primary.main },
                      { name: t("user.registered"), data: graphData.new_users_count.series_registered_user, color: theme.palette.secondary.main }
                    ]}
                    labels={graphData.new_users_count.labels}
                    handleClick={() => { }}
                    options={monthGraphOptions}
                    title=""
                    categories={[]}
                  />
                  :
                  <Box width="100%" display="flex" justifyContent="center">
                    <CircularProgress sx={{ m: 5 }} />
                  </Box>
                }
              </Card>
            </Grid>
            <Grid size={12}>
              <Card sx={{ overflowX: "auto" }} >
                <Typography p={2} variant="h5">{t("stats.created-excursions")}</Typography>

                {graphData ?
                  <BarChart
                    height="295px"
                    width="100%"
                    series={[
                      { name: t("organisations.hps"), data: graphData.izleti_count.series_hps, color: theme.palette.primary.main },
                      { name: t("user.registered"), data: graphData.izleti_count.series_registered_user, color: theme.palette.secondary.main }
                    ]}
                    labels={graphData.izleti_count.labels}
                    handleClick={() => { }}
                    options={monthGraphOptions}
                    title=""
                    categories={[]}
                  />
                  :
                  <Box width="100%" display="flex" justifyContent="center">
                    <CircularProgress sx={{ m: 5 }} />
                  </Box>
                }
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </AppPage>
  );
}

export default HpsStatistikaPage;
